import React from 'react'
import { Link } from 'react-router-dom'
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  GraduationCap, 
  Users, 
  Calendar,
  ExternalLink 
} from 'lucide-react'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const quickLinks = [
    { name: 'Inicio', href: '/' },
    { name: 'Nosotros', href: '/nosotros' },
    { name: 'ExpoGardenias', href: '/expogardenias' },
    { name: 'Areas', href: '/areas' },
    { name: 'Bienestar Escolar', href: '/bienestar' },
    { name: 'Revistas', href: '/revistas' },
    { name: 'Admisiones', href: '/admisiones' },
    { name: 'Contacto', href: '/contacto' },
  ]

  const importantLinks = [
    { 
      name: 'Portal Colombia Aprende', 
      href: 'https://contenidos.colombiaaprende.edu.co/',
      external: true 
    },
    { 
      name: 'Pruebas Saber - ICFES', 
      href: 'https://www.barranquilla.gov.co/educacion/pruebas-saber-icfes',
      external: true 
    },
    { 
      name: 'Remisiones Coordinación', 
      href: 'https://forms.gle/o2FFY6ingp84TpTm8',
      external: true 
    },
  ]

  return (
    <footer className="bg-gardenia-dark text-white">
      <div className="container-custom">
        {/* Sección principal del footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Información de la institución */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <GraduationCap className="h-10 w-10 text-gardenia-red" />
                <div className="absolute -top-1 -right-1 h-4 w-4 bg-gardenia-gold rounded-full opacity-80"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white">
                  Las Gardenias
                </span>
                <span className="text-xs text-gardenia-gold font-medium">
                  Paz, Educación y Progreso
                </span>
              </div>
            </Link>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Institución Educativa Distrital comprometida con la formación integral 
              de nuestros estudiantes desde 2017, brindando educación de calidad 
              en Barranquilla.
            </p>
            <div className="flex flex-col space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <Users className="h-4 w-4 text-gardenia-red flex-shrink-0" />
                <span><span className="text-gardenia-gold font-semibold">2,200</span> Estudiantes matriculados</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <Users className="h-4 w-4 text-gardenia-green flex-shrink-0" />
                <span><span className="text-gardenia-gold font-semibold">70</span> Miembros del equipo</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <Calendar className="h-4 w-4 text-gardenia-gold flex-shrink-0" />
                <span>En funciones desde <span className="text-gardenia-gold font-semibold">2017</span></span>
              </div>
            </div>
          </div>

          {/* Enlaces rápidos */}
          <div>
            <h3 className="text-lg font-semibold text-gardenia-gold mb-6">
              Enlaces Rápidos
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-gardenia-gold transition-colors duration-300 flex items-center space-x-2 group"
                  >
                    <span className="w-1 h-1 bg-gardenia-red rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Enlaces importantes */}
          <div>
            <h3 className="text-lg font-semibold text-gardenia-gold mb-6">
              Enlaces de Interés
            </h3>
            <ul className="space-y-3">
              {importantLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    target={link.external ? "_blank" : "_self"}
                    rel={link.external ? "noopener noreferrer" : ""}
                    className="text-gray-300 hover:text-gardenia-gold transition-colors duration-300 flex items-center space-x-2 group"
                  >
                    <span className="w-1 h-1 bg-gardenia-red rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span>{link.name}</span>
                    {link.external && (
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Información de contacto */}
          <div>
            <h3 className="text-lg font-semibold text-gardenia-gold mb-6">
              Contacto
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-gardenia-red flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Calle 98C # 1E-99<br />
                    Barranquilla, Colombia
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-gardenia-green flex-shrink-0" />
                <a href="tel:+57" className="text-gray-300 text-sm hover:text-gardenia-gold transition-colors">
                  +57
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gardenia-gold flex-shrink-0" />
                <a href="mailto:info@lasgardenias.edu.co" className="text-gray-300 text-sm hover:text-gardenia-gold transition-colors">
                  info@lasgardenias.edu.co
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-gardenia-red flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Lunes a Viernes<br />
                    6:30 AM - 6:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Línea divisoria */}
        <div className="border-t border-gray-700"></div>

        {/* Copyright */}
        <div className="py-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-center md:text-left">
            <p className="text-gray-400 text-sm">
              © {currentYear} Institución Educativa Distrital Las Gardenias. 
              Todos los derechos reservados.
            </p>
          </div>
          <div className="text-center md:text-right">
            <p className="text-gray-400 text-sm">
              Desarrollado con ❤️ para la educación de calidad
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer